import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  HeartPulse, LayoutDashboard, CalendarRange, Users, UserCog, IdCard, LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/panel", label: "Panel del día", icon: LayoutDashboard },
  { to: "/panel/disponibilidad", label: "Disponibilidad", icon: CalendarRange, doctorOnly: true },
  { to: "/panel/pacientes", label: "Pacientes", icon: Users, doctorOnly: true },
  { to: "/panel/asistentes", label: "Asistentes", icon: UserCog, doctorOnly: true },
  { to: "/panel/perfil", label: "Mi perfil", icon: IdCard, doctorOnly: true },
];

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items = NAV.filter((n) => !n.doctorOnly || user?.role === "doctor");

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 flex-col border-r border-[#E2E8F0] bg-white lg:flex">
        <div className="flex items-center gap-2 border-b border-[#E2E8F0] px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0284C7] text-white">
            <HeartPulse className="h-4 w-4" strokeWidth={2.6} />
          </div>
          <span className="text-base font-extrabold tracking-tight text-slate-900">SaludCitaRD</span>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {items.map((n) => {
            const active = pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`sidebar-${n.to.split("/").pop() || "panel"}`}
                className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  active ? "bg-sky-50 text-[#0284C7]" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-[#E2E8F0] p-4">
          <div className="mb-3 px-2">
            <div className="text-sm font-extrabold text-slate-900">{user?.name}</div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
              {user?.role === "assistant" ? "Asistente" : "Médico"}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-50"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        {/* mobile top bar */}
        <div className="flex items-center gap-3 overflow-x-auto border-b border-[#E2E8F0] bg-white px-4 py-3 lg:hidden">
          {items.map((n) => {
            const active = pathname === n.to;
            return (
              <Link key={n.to} to={n.to} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${active ? "bg-sky-50 text-[#0284C7]" : "text-slate-500"}`}>
                {n.label}
              </Link>
            );
          })}
        </div>
        <main className="mx-auto max-w-6xl px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
